import React, { useMemo, useState, useEffect } from "react";
import Switch from "@/components/common/Switch/Switch";
import ButtonWithDropdownOption from "@/components/common/ButtonWithDropdownOption/Button";
import AngleArrow from "@assets/icons/common/angle_down.svg?react";
import Tooltip from "@/components/common/Tooltip/Tooltip";
import "./StatTesting.css";
import { STAT_TESTING_OPTIONS } from "./StatTesting.constant";
import { useDispatch, useSelector } from "react-redux";
import type { AppDispatch, RootState } from "@/store/store";
import { dequal } from "dequal";
import { setPendingSelectionIfChanged } from "@/store/slices/pendingSelection/pendingSelectionSlice";
import { selectStatTestingConfig } from "@/store/slices/screenConfig/selectors";
import type { StatTestingOptionEntry } from "@/store/slices/screenConfig/moduleConfig.types";
import classNames from "classnames";

interface StatTestingProps {}

function getDaysDifference(startDate: any, endDate: any) {
  if (!startDate || !endDate) {
    return 0;
  }
  const start = new Date(startDate);
  const end = new Date(endDate);
  const differenceInTime = end.getTime() - start.getTime();
  const differenceInDays = differenceInTime / (1000 * 3600 * 24);
  return differenceInDays;
}

/**
 * Resolves whether an option is disabled at runtime, and what message to show.
 * Returns `null` if the option is enabled (no disable condition met).
 * Returns the disable message string if any condition applies.
 */
function resolveRuntimeDisabledMessage(
  entry: StatTestingOptionEntry,
  daysDifference: number,
  period: string | undefined,
  mode: string | undefined
): string | null {
  // Period-based restriction only applies in TREND mode
  if (
    mode === "TREND" &&
    entry.disableForPeriods &&
    period &&
    entry.disableForPeriods.periods.includes(period)
  ) {
    return entry.disableForPeriods.message;
  }
  if (entry.disableWhenDaysExceed && daysDifference > entry.disableWhenDaysExceed.days) {
    return entry.disableWhenDaysExceed.message;
  }
  return null;
}

function StatTesting({}: StatTestingProps) {
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const dispatch = useDispatch<AppDispatch>();

  const { period, startDate, endDate, statTestingEnabled, mode } = useSelector(
    (state: RootState) => ({
      statTestingEnabled: state.userSelection.statTest || "NONE",
      period: state.userSelection.period,
      startDate: state.userSelection.periodStartDate,
      endDate: state.userSelection.periodEndDate,
      mode: state.userSelection.mode,
    }),
    (prev, next) => dequal(prev, next)
  );

  const statTestingConfig = useSelector(selectStatTestingConfig);

  function handleSelect(selectedValue: string) {
    dispatch(setPendingSelectionIfChanged({ statTest: selectedValue } as any));
  }

  const daysDifference = useMemo(
    () => getDaysDifference(startDate, endDate),
    [startDate, endDate]
  );

  const isStatTestingOn =
    statTestingEnabled === "POP" ||
    statTestingEnabled === "YOY" ||
    statTestingEnabled === "PTP";

  /**
   * When the user toggles stat testing ON, select the first non-hidden,
   * non-disabled option from the config (respecting the current config order).
   * Falls back to "POP" if nothing is found (safety net only).
   */
  const defaultOnSelection = useMemo(() => {
    const firstEnabled = statTestingConfig?.find(
      (e) => e.hidden !== true && e.enabled !== false
    );
    return firstEnabled?.id ?? "POP";
  }, [statTestingConfig]);

  // If no config or empty array → stat testing not supported on this screen; render nothing
  if (!statTestingConfig || statTestingConfig.length === 0) {
    return null;
  }

  // Build visible option list (non-hidden entries merged with STAT_TESTING_OPTIONS metadata)
  const visibleOptions = STAT_TESTING_OPTIONS.filter((opt) => {
    const configEntry = statTestingConfig.find((e) => e.id === opt.id);
    // If not in config → not visible. If hidden → not visible.
    return configEntry && configEntry.hidden !== true;
  });

  // Find active label from config
  const activeLabelText = isStatTestingOn
    ? statTestingConfig.find((e) => e.id === statTestingEnabled)?.activeLabel ?? ""
    : "";

  return (
    <div className="stat-testing-wrapper">
      <ButtonWithDropdownOption
        className="stat-testing-popup-container"
        isDisabled={!isStatTestingOn}
        trigger={
          <div
            className={classNames(
              "text-text-500 text-xs cursor-pointer flex gap-1 items-center bg-white px-2 py-1"
            )}
          >
            <Switch
              checked={isStatTestingOn}
              handleChange={() =>
                handleSelect(isStatTestingOn ? "NONE" : defaultOnSelection)
              }
            />
            <span className="pl-1 font-medium">
              Stat Testing{" "}
              {activeLabelText ? `(${activeLabelText})` : ""}
            </span>
            {isStatTestingOn && (
              <AngleArrow
                height={12}
                className={
                  true
                    ? "stat-testing-arrow stat-testing-arrow-open relative ml-[-4px]"
                    : "stat-testing-arrow  ml-[-4px]"
                }
              />
            )}
          </div>
        }
        onShow={() => {
          setIsTooltipVisible(true);
        }}
        onHide={() => {
          setIsTooltipVisible(false);
        }}
      >
        <ul className="stat-testing-popup">
          {visibleOptions.map((option) => {
            const configEntry = statTestingConfig.find((e) => e.id === option.id)!;

            // 1. Statically disabled via config (enabled === false)
            if (configEntry.enabled === false) {
              return (
                <Tooltip
                  key={option.id}
                  contentList={[configEntry.disabledMessage ?? ""]}
                  placement="left"
                  classname="stat-testing-disabled-message"
                  offset={[0, 20]}
                >
                  <li className="stat-testing-disabled-option">
                    <div className="mt-1">{option.icon}</div>
                    <div>
                      <div className="stat-testing-title">{option.title}</div>
                      <div className="stat-testing-description">
                        {option.description}
                      </div>
                    </div>
                  </li>
                </Tooltip>
              );
            }

            // 2. Runtime disable check (days exceeded or period restriction)
            const runtimeMessage = resolveRuntimeDisabledMessage(
              configEntry,
              daysDifference,
              period,
              mode
            );
            if (runtimeMessage !== null) {
              return (
                <Tooltip
                  key={option.id}
                  contentList={[runtimeMessage]}
                  placement="left"
                  classname="stat-testing-disabled-message"
                  offset={[0, 20]}
                >
                  <li className="stat-testing-disabled-option">
                    <div className="mt-1">{option.icon}</div>
                    <div>
                      <div className="stat-testing-title">{option.title}</div>
                      <div className="stat-testing-description">
                        {option.description}
                      </div>
                    </div>
                  </li>
                </Tooltip>
              );
            }

            // 3. Enabled — clickable
            return (
              <li
                key={option.id}
                className={
                  option.id === statTestingEnabled ? "active-stat-testing" : ""
                }
                onClick={() => handleSelect(option.id)}
              >
                <div className="mt-1">{option.icon}</div>
                <div>
                  <div className="stat-testing-title">{option.title}</div>
                  <div className="stat-testing-description">
                    {option.description}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      </ButtonWithDropdownOption>
    </div>
  );
}

export default StatTesting;
